import { useState } from "react";
import { Bell, CheckCheck, X } from "lucide-react";
import { getNotifications, markNotification } from "./lib/api";
import type { CrmNotification } from "./types";
import "./NotificationCenter.css";
export function NotificationCenter({
  items,
  setItems,
  onNavigate,
}: {
  items: CrmNotification[];
  setItems: (items: CrmNotification[]) => void;
  onNavigate: (path: string) => void;
}) {
  const [open, setOpen] = useState(false),
    [working, setWorking] = useState<string | null>(null),
    [error, setError] = useState(""),
    [filter, setFilter] = useState<"ALL" | "UNREAD" | "READ">("ALL");
  const unread = items.filter((item) => item.state === "UNREAD").length;
  const visible = items.filter(
    (item) =>
      item.state !== "DISMISSED" &&
      (filter === "ALL" || item.state === filter),
  );
  async function read(item: CrmNotification) {
    if (working) return;
    setWorking(item.id);
    setError("");
    try {
      const saved =
        item.state === "UNREAD"
          ? await markNotification(item.id, "READ")
          : item;
      setItems(items.map((value) => (value.id === saved.id ? saved : value)));
      setOpen(false);
      onNavigate(saved.deepLink);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message.replaceAll("_", " ")
          : "Notification could not be opened.",
      );
    } finally {
      setWorking(null);
    }
  }
  async function all() {
    if (working) return;
    setWorking("all");
    setError("");
    try {
      const saved = await Promise.all(
        items
          .filter((value) => value.state === "UNREAD")
          .map((item) => markNotification(item.id, "READ")),
      );
      const map = new Map(saved.map((item) => [item.id, item]));
      setItems(items.map((item) => map.get(item.id) || item));
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message.replaceAll("_", " ")
          : "Notifications could not be updated.",
      );
    } finally {
      setWorking(null);
    }
  }
  async function toggle() {
    if (open) {
      setOpen(false);
      return;
    }
    setError("");
    try {
      setItems(await getNotifications());
      setOpen(true);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message.replaceAll("_", " ")
          : "Notifications could not be loaded.",
      );
      setOpen(true);
    }
  }
  return (
    <div className="notification-center">
      <button
        className="icon-btn notification-bell"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ""}`}
        aria-expanded={open}
        onClick={() => void toggle()}
      >
        <Bell size={18} />
        {unread > 0 && <b>{unread > 99 ? "99+" : unread}</b>}
      </button>
      {open && (
        <aside className="notification-panel">
          <header>
            <div>
              <span>NOTIFICATIONS</span>
              <h2>Attention queue</h2>
            </div>
            <button
              aria-label="Close notifications"
              onClick={() => setOpen(false)}
            >
              <X />
            </button>
          </header>
          <div className="notification-actions">
            <span>{unread} unread</span>
            <select
              aria-label="Filter notifications"
              value={filter}
              onChange={(event) =>
                setFilter(event.target.value as typeof filter)
              }
            >
              <option value="ALL">All</option>
              <option value="UNREAD">Unread</option>
              <option value="READ">Read</option>
            </select>
            <button onClick={() => setFilter("ALL")}>Clear filter</button>
            <button
              disabled={!unread || Boolean(working)}
              onClick={() => void all()}
            >
              <CheckCheck size={14} />
              Mark all read
            </button>
          </div>
          {error && (
            <div className="composer-error" role="alert">
              {error}
            </div>
          )}
          <div className="notification-list">
            {visible.length === 0 ? (
              <p>No notifications.</p>
            ) : (
              visible.map((item) => (
                  <button
                    key={item.id}
                    className={item.state.toLowerCase()}
                    disabled={working === item.id}
                    onClick={() => void read(item)}
                  >
                    <i />
                    <div>
                      <strong>{item.title}</strong>
                      <span>{item.detail}</span>
                      <small>{new Date(item.createdAt).toLocaleString()}</small>
                    </div>
                  </button>
                ))
            )}
          </div>
        </aside>
      )}
    </div>
  );
}
